import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PageTitle from "@/components/PageTitle";
import { OwnerSplitBar, OwnerKey, useOwnerColours } from "@/components/dashboard/OwnerSplit";
import { ShieldAlert, RefreshCw, Filter, Clock3, CalendarClock, ChevronRight, Handshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_ROUTE = {
  "PO Received": "/po-received",
  "Procurement": "/procurement",
  "Manufacturing": "/manufacturing",
  "Quality Control": "/qc-check",
  "Dispatch": "/dispatch",
};

const BUCKET_ORDER = ["0-2", "3-7", "8-14", "15+"];
const BUCKET_LABEL = { "0-2": "0–2 days", "3-7": "3–7 days", "8-14": "8–14 days", "15+": "15+ days" };

/**
 * Blocked Board (BLOCKING_REDESIGN_PLAN.md §7) — the weekly review agenda.
 *
 * Not "why is this late?" but "whose court is the ball in?": every open block
 * in the pipeline, external owners first, oldest first, with slipped promises
 * called out in red. Walk it top-down; every external row earns a chase and a
 * fresh promise date, every 15+ day internal row earns a management decision.
 */
export default function BlockedBoardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const res = await axios.get(`${API}/blocked-board`);
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load the board");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const needle = q.trim().toLowerCase();
    return all.filter((r) =>
      (!ownerFilter || r.owner === ownerFilter || (ownerFilter === "_none" && !r.owner)) &&
      (!stageFilter || r.stage === stageFilter) &&
      (!overdueOnly || r.overdue) &&
      (!needle || [r.po_number, r.client, r.party, r.item_description, r.part_number, r.reason]
        .some((v) => String(v || "").toLowerCase().includes(needle)))
    );
  }, [data, ownerFilter, stageFilter, overdueOnly, q]);

  const stages = useMemo(
    () => Array.from(new Set((data?.rows || []).map((r) => r.stage))).filter(Boolean), [data]);

  // Owner rows in the shared split-bar shape (same look as the dashboard's
  // Waiting On card). The server already ranks them, outside first.
  const ownerRows = useMemo(() => (data?.rollup || []).map((r) => ({
    key: r.owner || "_none", owner: r.owner, label: r.label || "No owner yet",
    type: r.owner ? (r.type === "external" ? "external" : "internal") : "unassigned",
    count: r.count || 0, oldest: r.oldest_days || 0, overdue: r.overdue || 0,
  })), [data]);
  const ownerColoured = useOwnerColours(ownerRows);

  if (loading && !data) {
    return <div className="p-6 text-sm text-[hsl(var(--muted-foreground))]">Loading the board…</div>;
  }
  if (err) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 text-center">
          <ShieldAlert className="w-6 h-6 mx-auto text-[hsl(var(--muted-foreground))] mb-2" />
          <p className="text-sm text-[hsl(var(--foreground))] font-medium">{err}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Ask an admin for Blocked Board access.</p>
        </div>
      </div>
    );
  }

  const rollup = data?.rollup || [];
  const totalOverdue = rollup.reduce((n, r) => n + (r.overdue || 0), 0);
  const promises = data?.promises || { kept: 0, broken: 0 };
  const promiseTotal = (promises.kept || 0) + (promises.broken || 0);

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="blocked-board">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <PageTitle
            color="#e11d48"
            title="Blocked Board"
            count={data?.open_blocks || 0}
            unit={(data?.open_blocks || 0) === 1 ? "open block" : "open blocks"}
          />
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 ml-[17px]">
            across {data?.blocked_items || 0} item{(data?.blocked_items || 0) === 1 ? "" : "s"}
            {totalOverdue > 0 && (
              <span className="text-red-500 font-semibold"> · {totalOverdue} promise{totalOverdue === 1 ? "" : "s"} slipped</span>
            )}
          </p>
        </div>
        <button
          type="button" onClick={load}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
          data-testid="board-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* ── Who we're waiting on — the SAME split bar + key as the dashboard's
          Waiting On card (2-Nov-2026), full width. Click an owner in the key
          to filter the table to them; click again to clear. ── */}
      {rollup.length > 0 ? (
        <div className="rounded-xl px-5 py-4 rl-glass rl-hero-glass flex flex-col gap-3" data-testid="board-owner-split">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70">
              <ShieldAlert className="w-3 h-3 text-red-500" />
              Waiting on
            </span>
            <span className="text-[10.5px] text-[hsl(var(--muted-foreground))]">
              {ownerFilter ? "Showing one owner · click it again to see all" : "Click an owner to show only their blocks"}
            </span>
          </div>
          <OwnerSplitBar rows={ownerColoured} selected={ownerFilter || null} height="h-4" testId="board-split-bar" />
          <OwnerKey
            rows={ownerColoured}
            selected={ownerFilter || null}
            onSelect={(k) => setOwnerFilter(k || "")}
            className="grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
            testIdPrefix="rollup"
          />
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-[hsl(var(--border))] p-8 text-center">
          <ShieldAlert className="w-6 h-6 mx-auto text-emerald-500 mb-2" />
          <p className="text-sm font-medium text-[hsl(var(--foreground))]">Nothing is blocked.</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Every line in the pipeline is moving — there's nobody to chase today.</p>
        </div>
      )}

      {/* ── Aging + promise keeping ── */}
      {rollup.length > 0 && (
        <div className="flex gap-2 flex-wrap items-stretch">
          <div className="flex gap-2 flex-wrap">
            {BUCKET_ORDER.map((b) => (
              <div key={b} className="px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] min-w-[92px]">
                <div className="text-lg font-semibold leading-none text-[hsl(var(--foreground))]">{data?.buckets?.[b] || 0}</div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-1 mt-0.5">
                  <Clock3 className="w-2.5 h-2.5" />{BUCKET_LABEL[b]}
                </div>
              </div>
            ))}
          </div>
          {promiseTotal > 0 && (
            <div className="px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
              <div className="text-lg font-semibold leading-none text-[hsl(var(--foreground))]">
                {Math.round((promises.kept / promiseTotal) * 100)}%
              </div>
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-1 mt-0.5">
                <Handshake className="w-2.5 h-2.5" />promises kept ({promises.kept}/{promiseTotal})
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Filters ── */}
      {rollup.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center">
          <Filter className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="PO, client, party, part…"
            className="h-8 px-2.5 text-xs rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none focus:ring-2 focus:ring-orange-500 min-w-[200px]"
            data-testid="board-search"
          />
          <select
            value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
            className="h-8 px-2 text-xs rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))]"
            data-testid="board-stage-filter"
          >
            <option value="">All stages</option>
            {stages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            type="button" onClick={() => setOverdueOnly((v) => !v)}
            className={`h-8 px-2.5 text-xs font-semibold rounded-md border transition-colors ${
              overdueOnly ? "bg-red-500/15 border-red-500 text-red-600 dark:text-red-400"
                          : "bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-red-500/50"
            }`}
            data-testid="board-overdue-filter"
          >
            Overdue promises only
          </button>
          {(ownerFilter || stageFilter || overdueOnly || q) && (
            <button type="button" onClick={() => { setOwnerFilter(""); setStageFilter(""); setOverdueOnly(false); setQ(""); }}
              className="h-8 px-2.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              Clear
            </button>
          )}
          <span className="text-[11px] text-[hsl(var(--muted-foreground))] ml-auto">{rows.length} shown</span>
        </div>
      )}

      {/* ── The table — one row per open block ── */}
      {rollup.length > 0 && (
        <div className="rounded-lg border border-[hsl(var(--border))] overflow-x-auto bg-[hsl(var(--card))]">
          <table className="w-full text-left" data-testid="board-table">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                <th className="px-3 py-2">Waiting on</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2">Stage</th>
                <th className="px-3 py-2 whitespace-nowrap">Age</th>
                <th className="px-3 py-2 whitespace-nowrap">Promised</th>
                <th className="px-3 py-2">Next action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const ext = r.owner_type === "external";
                return (
                  <tr
                    key={r.block_id}
                    className="border-b border-[hsl(var(--border))]/50 hover:bg-[hsl(var(--muted))]/30 cursor-pointer align-top"
                    onClick={() => navigate(STAGE_ROUTE[r.stage] || "/")}
                    data-testid={`board-row-${r.block_id}`}
                  >
                    <td className="px-3 py-2">
                      <span className={`rl-pill inline-flex items-center gap-1 text-[10.5px] font-medium rounded-full pl-1.5 pr-2 py-px border whitespace-nowrap ${
                        !r.owner ? "bg-[hsl(var(--muted))]/60 border-dashed border-[hsl(var(--muted-foreground))]/50 text-[hsl(var(--muted-foreground))]"
                        : ext ? "bg-amber-500/10 border-amber-500/50 text-amber-700 dark:text-amber-300"
                              : "bg-slate-500/10 border-slate-500/40 text-slate-700 dark:text-slate-200"}`}>
                        <span className="font-mono-data font-bold text-[9.5px]">{r.owner || "SET OWNER"}</span>
                      </span>
                      <div className="text-[11px] text-[hsl(var(--foreground))] mt-0.5">{r.party || r.owner_label}</div>
                    </td>
                    <td className="px-3 py-2 max-w-[280px]">
                      <div className="text-[12px] text-[hsl(var(--foreground))]">{r.reason}</div>
                      {r.note && r.note !== r.reason && (
                        <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] break-words">{r.note}</div>
                      )}
                      {r.code && <span className="font-mono-data text-[9.5px] text-[hsl(var(--muted-foreground))]">{r.code}</span>}
                    </td>
                    <td className="px-3 py-2 max-w-[240px]">
                      <div className="text-[12px] font-medium text-[hsl(var(--foreground))] truncate">{r.item_description}</div>
                      <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] font-mono-data">
                        {r.po_number}{r.line_number ? `·L${r.line_number}` : ""} · {r.client}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-[hsl(var(--muted-foreground))] whitespace-nowrap">{r.stage}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`text-[12px] font-semibold ${(r.days || 0) >= 15 ? "text-red-500" : "text-[hsl(var(--foreground))]"}`}>
                        {r.days === 0 ? "today" : `${r.days}d`}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.expected_unblock_at ? (
                        <span className={`inline-flex items-center gap-1 text-[11px] ${r.overdue ? "text-red-500 font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}>
                          <CalendarClock className="w-2.5 h-2.5" />{r.expected_unblock_at}{r.overdue ? " · overdue" : ""}
                        </span>
                      ) : (
                        <span className="text-[11px] text-[hsl(var(--muted-foreground))]/60">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-[200px]">
                      {r.next_action ? (
                        <span className="text-[11px] text-[hsl(var(--foreground))]">
                          {r.next_action}
                          {r.next_action_date && <span className="text-[hsl(var(--muted-foreground))] font-mono-data"> · {r.next_action_date}</span>}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))]/70">
                          none set <ChevronRight className="w-2.5 h-2.5" />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
                  Nothing matches those filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Who cost us the most time (last months) ── */}
      {(data?.by_party || []).length > 0 && (
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-2">
            Blocked days by party — resolved blocks, recent months
          </div>
          <div className="flex gap-2 flex-wrap">
            {data.by_party.slice(0, 12).map((p) => (
              <div key={p.party} className="px-2.5 py-1.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
                <span className="text-[12px] font-semibold text-[hsl(var(--foreground))]">{p.days}d</span>
                <span className="text-[11px] text-[hsl(var(--muted-foreground))] ml-1.5">{p.party}</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]/70 ml-1">({p.blocks})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
